/**
 * useSemanticSearch — dual-source Phase B hook for TASK-022.
 *
 * Calls the backend `searchDocumentsHttp` callable behind a 350 ms debounce.
 * Phase A (client-side keyword search) continues to render instantly and is
 * NOT replaced — this hook is enrichment. On any error, returns an empty
 * result set silently so the page falls back to Phase A.
 *
 * Gated by `VITE_FEATURE_SEMANTIC_SEARCH`. When the flag is off (default in
 * dev until Phase 8 flips it), the hook returns `enabled: false` and does
 * not fire any calls.
 */

import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useDebounce } from './useDebounce';

const DEBOUNCE_MS = 350;
const FLAG = (import.meta.env.VITE_FEATURE_SEMANTIC_SEARCH || '').toString();
const FEATURE_ENABLED = FLAG === 'true' || FLAG === '1';

export interface SemanticSearchResult {
  documentId: string;
  caseFileId: string;
  fileName: string;
  documentType: string;
  category: string;
  relevanceScore: number;
  snippets: string[];
  uploadedAt: { _seconds: number; _nanoseconds: number } | string | null;
}

export interface SemanticSearchRequest {
  query: string;
  caseFileId?: string;
  documentType?: string;
  limit?: number;
}

interface SearchResponse {
  success: boolean;
  results: SemanticSearchResult[];
  totalCount: number;
}

export type SemanticSearchStatus = 'idle' | 'loading' | 'success' | 'error' | 'disabled';

export interface UseSemanticSearchReturn {
  enabled: boolean;
  status: SemanticSearchStatus;
  results: SemanticSearchResult[];
  error: string | null;
}

const searchDocumentsCallable = httpsCallable<SemanticSearchRequest, SearchResponse>(
  functions,
  'searchDocumentsHttp'
);

export function useSemanticSearch(
  request: SemanticSearchRequest
): UseSemanticSearchReturn {
  const debouncedQuery = useDebounce(request.query, DEBOUNCE_MS);

  const [status, setStatus] = useState<SemanticSearchStatus>(
    FEATURE_ENABLED ? 'idle' : 'disabled'
  );
  const [results, setResults] = useState<SemanticSearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Each query supersedes the previous — track the latest request so an
  // in-flight call from an older query can't overwrite newer results.
  const latestRequestId = useRef(0);

  useEffect(() => {
    if (!FEATURE_ENABLED) return;

    const trimmed = debouncedQuery.trim();
    if (!trimmed) {
      setStatus('idle');
      setResults([]);
      setError(null);
      return;
    }

    const myRequestId = ++latestRequestId.current;
    setStatus('loading');
    setError(null);

    // Only include fields with defined values. Firebase httpsCallable serializes
    // `undefined` as JSON `null` on the wire, which the backend's strict
    // type checks reject ("caseFileId must be a string").
    const payload: SemanticSearchRequest = { query: trimmed };
    if (request.caseFileId != null) payload.caseFileId = request.caseFileId;
    if (request.documentType != null) payload.documentType = request.documentType;
    if (request.limit != null) payload.limit = request.limit;

    searchDocumentsCallable(payload)
      .then((response) => {
        if (myRequestId !== latestRequestId.current) return; // stale
        const data = response.data;
        if (!data || !data.success) {
          setStatus('error');
          setError('Search backend returned a failure');
          setResults([]);
          return;
        }
        setStatus('success');
        setResults(data.results || []);
      })
      .catch((err: unknown) => {
        if (myRequestId !== latestRequestId.current) return;
        const message = err instanceof Error ? err.message : String(err);
        // Silent fallback per design doc §2.5 — log only, no user-facing surface.
        console.warn('useSemanticSearch: silent fallback to Phase A', message);
        setStatus('error');
        setError(message);
        setResults([]);
      });
  }, [
    debouncedQuery,
    request.caseFileId,
    request.documentType,
    request.limit,
  ]);

  return { enabled: FEATURE_ENABLED, status, results, error };
}
